import React, { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { ScrollArea, ScrollBar } from '@/Components/ui/scroll-area'
import { Button } from '@/Components/ui/button'
import { FormControl } from '../ui/form'

const parseTimeToDate = (timeStr) => {
    const [time, modifier] = timeStr.split(' ')
    if (!time || !modifier) return null

    let [hours, minutes] = time.split(':').map(Number)
    if (modifier === 'PM' && hours !== 12) hours += 12
    if (modifier === 'AM' && hours === 12) hours = 0

    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    return date
}

const convertTo24HourFormat = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return ''

    const [time, modifier] = timeStr.split(' ')
    if (!time || !modifier) return ''

    let [hours, minutes] = time.split(':').map(Number)
    if (modifier === 'PM' && hours < 12) hours += 12
    if (modifier === 'AM' && hours === 12) hours = 0

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
}

const convert24To12HourFormat = (timeStr) => {
    if (!timeStr) return ''
    const [hourStr, minuteStr] = timeStr.split(':')
    let hour = parseInt(hourStr, 10)
    const minute = minuteStr.padStart(2, '0')

    const ampm = hour >= 12 ? 'PM' : 'AM'
    hour = hour % 12 || 12 // convert 0 to 12

    return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`
}

const TimePicker = ({ value, handleChanges, className }) => {
    const [time, setTime] = useState(() => {
        if (!value) return ''
        return convert24To12HourFormat(value)
    })
    function handleTimeChange(type, val) {
        let [hour = '12', minute = '00', ampm = 'AM'] = time.split(/[: ]/)

        if (type === 'hour') hour = val.padStart(2, '0')
        if (type === 'minute') minute = val.padStart(2, '0')
        if (type === 'ampm') ampm = val

        const newTime = `${hour}:${minute} ${ampm}`
        setTime(newTime)

        const formatted = convertTo24HourFormat(newTime)
        handleChanges(formatted) // sends 'HH:mm:ss' string
    }

    const timeAsDate = time ? parseTimeToDate(time) : null

    return (
        <Popover className="w-full">
            <PopoverTrigger asChild>
                <FormControl>
                    <Button
                        variant={'outline'}
                        className={cn('w-full pl-3 py-6 text-left font-normal justify-start', !timeAsDate && 'text-muted-foreground', className)}
                    >
                        {timeAsDate ? format(timeAsDate, 'hh:mm aa') : <span className="text-gray-300">Select time</span>}
                    </Button>
                </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0">
                <div className="sm:flex">
                    <div className="flex flex-col sm:flex-row sm:h-[300px] divide-y sm:divide-y-0 sm:divide-x max-h-[225px] w-full">
                        <ScrollArea className="">
                            <div className="flex sm:flex-col p-2">
                                {Array.from({ length: 12 }, (_, i) => i + 1)
                                    .reverse()
                                    .map((hour) => (
                                        <Button
                                            key={hour}
                                            size="icon"
                                            variant={timeAsDate && timeAsDate.getHours() % 12 === hour % 12 ? 'default' : 'ghost'}
                                            className="sm:w-full shrink-0 aspect-square"
                                            onClick={() => handleTimeChange('hour', hour.toString())}
                                        >
                                            {hour}
                                        </Button>
                                    ))}
                            </div>
                            <ScrollBar orientation="horizontal" className="sm:hidden" />
                        </ScrollArea>

                        {/* Minute Picker */}
                        <ScrollArea className="">
                            <div className="flex sm:flex-col p-2">
                                {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
                                    <Button
                                        key={minute}
                                        size="icon"
                                        variant={timeAsDate && timeAsDate.getMinutes() === minute ? 'default' : 'ghost'}
                                        className="sm:w-full shrink-0 aspect-square"
                                        onClick={() => handleTimeChange('minute', minute.toString())}
                                    >
                                        {minute.toString().padStart(2, '0')}
                                    </Button>
                                ))}
                            </div>
                            <ScrollBar orientation="horizontal" className="sm:hidden" />
                        </ScrollArea>

                        {/* AM/PM Picker */}
                        <ScrollArea>
                            <div className="">
                                {['AM', 'PM'].map((ampm) => (
                                    <Button
                                        key={ampm}
                                        size="icon"
                                        variant={
                                            timeAsDate &&
                                            ((ampm === 'AM' && timeAsDate.getHours() < 12) || (ampm === 'PM' && timeAsDate.getHours() >= 12))
                                                ? 'default'
                                                : 'ghost'
                                        }
                                        className="sm:w-full shrink-0 aspect-square"
                                        onClick={() => handleTimeChange('ampm', ampm)}
                                    >
                                        {ampm}
                                    </Button>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}

export default TimePicker
